import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { saveAs } from 'file-saver';
import { forkJoin, Observable, of, pipe } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { NSCommonConstants, NSErrors } from '@ruby/configs/common.constants';
import { NSRateResultsConstant } from '@ruby/configs/rate-inquiry.constants';
import { RateInquiryService } from '@ruby/core/http/rate-inquiry/rate-inquiry.service';
import { IRate, IRateDetail, IRateInquiry } from '@ruby/modules/rate-inquiry/models/rate-inquiry.interface';
import * as RateCoreActions from '@ruby/modules/rate-inquiry/store/actions/rate-core.actions';
import { IError } from '@ruby/shared/models/request/error.interface';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Injectable()
export class RateCoreEffects {

  searchRateInquiry$ = createEffect(() => this.actions$.pipe(
    ofType(RateCoreActions.searchInquiry),
    pipe(
      map(action => {
        const responseList: Array<Observable<Array<IRate>>> = [];
        action.data.forEach((inquiry: IRateInquiry, index) => {
          responseList.push(this.rateSvc.searchRateInquiry(inquiry).pipe(
            map((resp: Array<IRate>) => {
              const isResultsEmpty = resp.every((value: IRate) => !value.rateDetails.length && value.rateReplyErrors.length);
              if (isResultsEmpty) {
                this.utilSvc.showWarnToastr(
                  NSRateResultsConstant.noRateResultsError.title,
                  `${ NSRateResultsConstant.noRateResultsError.message } ${ index + 1 }`,
                  NSCommonConstants.closeButtonToastrConfig
                );
                this.store.dispatch(RateCoreActions.updateLayout({ data: true }));
              } else {
                resp[0].rateDetails.forEach((item: IRateDetail) => {
                  item.requestInfo = inquiry;
                });
              }
              return resp;
            }),
            catchError((error: IError) => {
              if (error && error.code === 'ACCESSNS_BACKEND_RATEINQUIRY_012') {
                this.utilSvc.showErrorToastr(`${ error.title } # ${ index + 1 }`,
                  error.message, NSCommonConstants.closeButtonToastrConfig);
              } else {
                this.utilSvc.showErrorToastr(`${ NSErrors.genericServiceError.title } # ${ index + 1 }`, NSErrors.genericServiceError.message);
              }
              this.store.dispatch(RateCoreActions.searchInquiryFailure({
                error: {
                  title: error.title,
                  message: error.message,
                  code: error.code,
                  status: error.status
                }
              }));
              return of([]);
            })
          ));
        });
        forkJoin(responseList).subscribe((data: Array<Array<IRate>>) => {
          data.forEach((response: Array<IRate>) => {
            response.forEach((resp: IRate) => {
              this.store.dispatch(RateCoreActions.searchInquirySuccess({ data: resp.rateDetails }));
            });
            if (response.length === 0) {
              this.store.dispatch(RateCoreActions.searchInquirySuccess({ data: [] }));
            }
          });
        });
      })
    )), { dispatch: false }
  );

  sendEmail$ = createEffect(() => this.actions$.pipe(
    ofType(RateCoreActions.sendRateInquiryEmail),
    exhaustMap(action =>
      this.rateSvc.sendEmailRateInquiry(action.data).pipe(
        map(() => {
          this.utilSvc.showSuccessToastr('Email Sent!');
          return RateCoreActions.sendRateInquiryEmailSuccess();
        }),
        catchError((error: IError) => {
          this.utilSvc.showErrorToastr(error.title, error.message);
          return of(RateCoreActions.sendRateInquiryEmailFailure({ error }));
        })
      )
    )
  ));

  //@TODO Fix this
  /* istanbul ignore next */
  exportFile$ = createEffect(() => this.actions$.pipe(
    ofType(RateCoreActions.exportFile),
    exhaustMap(action => this.rateSvc.exportFile(action.data).pipe(
      map((pdfData: Blob) => {
        saveAs(pdfData, action.data.filename);
        return RateCoreActions.exportFileSuccess();
      }),
      catchError((error: IError) => {
        this.utilSvc.showErrorToastr(error.title, error.message);
        return of(RateCoreActions.exportFileFailure({ error }));
      }))
    )
  ));

  constructor(
    private actions$: Actions,
    private rateSvc: RateInquiryService,
    private store: Store,
    private utilSvc: UtilsService
  ) { }
}
